import React, { useEffect, useState } from "react";
import { IActionHandler, MessageDisplayType, UserProfile } from "@preveil-api";
import {
  Account, useFetchWatchedActivity, useAppSelector, DRIVE_MANAGER_STATE, useAppDispatch, GlobalErrorMessages, DriveErrorMessages, DriveWatchedMessages, Message, MessageAnchors,
  MessageHandlerDisplayType, Pagination, WatchedLogsEntity, DriveLimits, isNumber, DriveWatchedErrorMessages, FilterStates, ActivityLogFilters, DateRangeFilter, FilterToDateRange,
  dayjs
} from "src/common";
import { Loading, Filters } from "src/components";
import { EmptyMessage } from ".";
import { Toolbar, ListView } from "./watch-activity";
import { uiActions, driveActions } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

type AllProps = {
    current_account: Account;
};

// NOTE: Set Default Date to last month
const date_range: DateRangeFilter = {
    start: FilterToDateRange.last_month.toDate(),
    end: dayjs().endOf("day").toDate()
};

function WatchedActivityComponent(props: AllProps) {
    const { current_account } = props;
    const dispatch = useAppDispatch();
    const drive_state = useAppSelector((state: RootState) => state.drive.drive_state);
    const pagination = useAppSelector((state: RootState) => state.drive.pagination);
    const [page, setPage] = useState<string>(DriveLimits.DRIVE_PAGE_DEFAULT);
    const [filter, setFilter] = useState<ActivityLogFilters>({ date_range });
    const [error_message, setErrorMessage] = useState<string | undefined>();
    const [is_loading, setLoading] = useState<boolean>(true);
    const { fetchWatchedActivity, resetWatchedActivity, watched_activity, error } = useFetchWatchedActivity(current_account);

    useEffect(() => {
        DriveWatchRequests.handleRefresh(true);
    }, []);

    // Description: Handles first load on Drive state and websocket ready
    useEffect(() => {
        (!!watched_activity) &&
            DriveWatchRequests.handleWatchNotificationsSuccess(watched_activity);

        if (!!error) {
            setErrorMessage(DriveWatchedErrorMessages.watched_list_failed);
            setLoading(false);
            resetWatchedActivity();
        }
    }, [watched_activity, error]);

    const DriveWatchRequests = {
        // Description: Handle fetch watch page either Root or descendants based or router parameters AND Refresh
        handleRefresh(reset_page: boolean = false) {
            setLoading(true);
            reset_page && ResetPage();
            fetchWatchedActivity(Number(reset_page ? DriveLimits.DRIVE_PAGE_DEFAULT : page), filter?.date_range, filter?.user_filter);
        },

        //  Description: Navigate to page passed
        handlePaging(new_page: string) {
            if (new_page !== page && isNumber(new_page)) {
                setLoading(true);
                fetchWatchedActivity(Number(new_page), filter?.date_range, filter?.user_filter);
                setPage(new_page);
            } else {
                DriveWatchRequests.handlePageErrorMessage({
                    message: GlobalErrorMessages.default, stack: {
                        error: DriveErrorMessages.error_paginating.replace(MessageAnchors.page_number, new_page)
                    }
                });
            }
        },

        // Description: filters items in list by name, user
        handleFilter(name_filter: string) {
            const new_filter = {
                ...filter,
                ...{ name_filter }
            };
            setFilter(new_filter);
        },

        handleUserFilter(user_profiles: UserProfile[]) {
            const user_filter = _.map(user_profiles, (user: UserProfile) => user.address.toLowerCase());
            if (!is_loading && !!watched_activity) { // NOTE: Filter after fully loaded UI
                ResetPage();
                setLoading(true);
                fetchWatchedActivity(Number(DriveLimits.DRIVE_PAGE_DEFAULT), filter?.date_range, user_filter);
                const new_filter = {
                    ...filter,
                    ...{ user_filter }
                };
                setFilter(new_filter);
            };
        },

        // Description: server side filter items by date range
        handleDateSelector(_date_range: DateRangeFilter) {
            // NOTE: Accounts for reset to default values (include null)
            const adjusted_date_range = _date_range.start && _date_range.end ? _date_range : undefined;
            const new_filter = {
                ...filter,
                ...{ date_range: _date_range }
            };
            setFilter(new_filter);
            ResetPage();
            setLoading(true);
            fetchWatchedActivity(Number(DriveLimits.DRIVE_PAGE_DEFAULT), adjusted_date_range, filter?.user_filter);
        },

        // Description: Update UI after loading new data
        handleWatchNotificationsSuccess(_watched_activity: WatchedLogsEntity) {
            setLoading(false);
            const total_page_rows = _watched_activity.entries.length;
            const total_count = _watched_activity.pagination_info.total_count;
            const total_rows = total_count || _watched_activity.entries.length;
            if (total_page_rows > 0 && total_rows > 0) {
                const pagination_item = new Pagination(Pagination.getPageIndex(page), total_page_rows, total_rows, pagination.pageSize).pagination_item;
                !!pagination_item && dispatch(driveActions.setPagination(pagination_item));
            } else {
                dispatch(driveActions.resetPagination());
            }
        },

        // Description: Handle all page / components top level errors
        handlePageErrorMessage(params: { message: string, stack?: any, display_type?: MessageDisplayType }) {
            const display_type = !!params.display_type ? params.display_type : MessageHandlerDisplayType.logger;
            dispatch(uiActions.handleRequestErrors(new Message(params.message, display_type), params.stack));
        }
    };
    //  Description: Handle all actions from Children forms
    function handlePageActions(action_obj: IActionHandler) {
        const callback = `handle${action_obj.actionType}`;
        if ((DriveWatchRequests as any)[callback] instanceof Function) {
            (DriveWatchRequests as any)[callback](action_obj.params);
        } else {
            const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
            DriveWatchRequests.handlePageErrorMessage({ message, stack: action_obj });
        }
    }

    // Description: Reset state supporting client side pagination
    function ResetPage() {
        setPage(DriveLimits.DRIVE_PAGE_DEFAULT);
        dispatch(driveActions.resetPagination());
    }

    function RenderWatch() {
        if (drive_state === DRIVE_MANAGER_STATE.ERROR) {
            return <EmptyMessage message={DriveWatchedMessages.watched_changes_empty} />;
        } else {
            return <div className="watched-wrapper">
                <Filters
                    current_account={current_account}
                    default_active={FilterStates.last_month}
                    is_loading={is_loading}
                    handleAction={handlePageActions} />
                <Toolbar
                    is_loading={is_loading}
                    pagination={pagination}
                    handleAction={handlePageActions} />
                {
                    !!watched_activity ?
                        <ListView
                            current_account={current_account}
                            is_loading={is_loading}
                            filter={filter}
                            watched_activity={watched_activity}
                            pagination={pagination}
                            handleAction={handlePageActions} /> :
                        is_loading ? <Loading className="in-place mt-5" /> :
                            <EmptyMessage message={(!!error_message ? error_message : undefined)} />

                }
            </div>;
        };
    }

    return RenderWatch();
}

export default React.memo(WatchedActivityComponent);